import {
  BarChart3,
  Brain,
  Calendar,
  ClipboardCheck,
  CreditCard,
  FileText,
  HelpCircle,
  LogIn,
  LogOut,
  Menu,
  Package,
  Settings,
  Shield,
  Target,
  TrendingUp,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { SUPPORT_STAFF_UID_STORAGE_KEY, useAuth } from '../../contexts/AuthContext';
import { usePostUpdateJourneyOptional } from '../../shared/hooks/usePostUpdateJourney';
import { usePermission } from '../../shared/hooks/usePermission';
import type { PermissionKey } from '../../shared/permissions';
import { RelixLockup } from '../brand/RelixLogo';
import { ProductSwitcher } from '../ProductSwitcher';
import { SupportPanel } from '../support/SupportPanel';
import { isMarketingThemeLockPath } from '../../contexts/ThemeContext';
import { ThemeToggle } from '../ThemeToggle';

type NavItem = {
  id: string;
  label: string;
  path: string;
  icon: React.ReactNode;
  /** When set, the nav item is hidden if the user lacks this permission. */
  requiredPermission?: PermissionKey;
};

const SOLUTIONS_ITEM: NavItem = {
  id: 'solutions',
  label: 'Solutions',
  path: '/solutions',
  icon: <Brain className="w-5 h-5" />,
};

const HELP_ITEM: NavItem = {
  id: 'help',
  label: 'Help',
  path: '/help',
  icon: <HelpCircle className="w-5 h-5" />,
};

const AUTH_ITEM: NavItem = {
  id: 'auth',
  label: 'Sign in',
  path: '/auth',
  icon: <LogIn className="w-5 h-5" />,
};

const PRICING_ITEM: NavItem = {
  id: 'pricing',
  label: 'Pricing',
  path: '/pricing',
  icon: <CreditCard className="w-5 h-5" />,
};

const APP_NAV_ITEMS: NavItem[] = [
  {
    id: 'analytics',
    label: 'Analytics',
    path: '/analytics',
    icon: <BarChart3 className="w-5 h-5" />,
  },
  {
    id: 'reports',
    label: 'Reports',
    path: '/reports',
    icon: <FileText className="w-5 h-5" />,
    requiredPermission: 'reports.access',
  },
  {
    id: 'calendar',
    label: 'Calendar',
    path: '/calendar',
    icon: <Calendar className="w-5 h-5" />,
  },
  {
    id: 'releases',
    label: 'Releases',
    path: '/releases',
    icon: <Package className="w-5 h-5" />,
  },
  {
    id: 'forecast',
    label: 'Forecast',
    path: '/forecast',
    icon: <TrendingUp className="w-5 h-5" />,
  },
  {
    id: 'commitment',
    label: 'Commitment',
    path: '/commitment',
    icon: <Target className="w-5 h-5" />,
  },
  {
    id: 'delivery-review',
    label: 'Delivery Review',
    path: '/delivery-review',
    icon: <ClipboardCheck className="w-5 h-5" />,
  },
  {
    id: 'settings',
    label: 'Settings',
    path: '/settings',
    icon: <Settings className="w-5 h-5" />,
  },
];

export const Navigation: React.FC = () => {
  const { user, initialized, signOut, currentOrg, authMeSucceeded, isSupportUser } = useAuth();
  const [showSupportPanel, setShowSupportPanel] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { navHighlightPath } = usePostUpdateJourneyOptional();
  const isOnboardingRoute = pathname === '/onboarding' || pathname.startsWith('/onboarding/');
  const showThemeToggle = !isMarketingThemeLockPath(pathname);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen]);

  const hideProductSwitcher =
    pathname === '/' ||
    pathname === '/calendar' ||
    pathname.startsWith('/calendar/') ||
    pathname === '/solutions' ||
    pathname.startsWith('/solutions/') ||
    pathname === '/pricing' ||
    pathname.startsWith('/pricing/') ||
    pathname === '/help' ||
    pathname.startsWith('/help/') ||
    pathname === '/auth' ||
    isOnboardingRoute;

  const reportsAccess = usePermission('reports.access');

  const navItems = useMemo((): NavItem[] => {
    if (!initialized) {
      return [SOLUTIONS_ITEM, HELP_ITEM];
    }
    if (user) {
      if (isOnboardingRoute) {
        return [];
      }
      // Filter items by `requiredPermission` so members never see Reports
      // (the route is also server-side gated via `reports.access`).
      return APP_NAV_ITEMS.filter((item) => {
        if (item.requiredPermission === 'reports.access') return reportsAccess.allowed;
        return true;
      });
    }
    // PRICING_ITEM temporarily hidden from header
    return [SOLUTIONS_ITEM, HELP_ITEM, AUTH_ITEM];
  }, [user, initialized, isOnboardingRoute, reportsAccess.allowed]);

  const handleLogout = useCallback(async () => {
    await signOut();
    navigate('/', { replace: true });
  }, [navigate, signOut]);

  /** Platform support: prefer live API flag; fall back to uid cache when GET /api/auth/me fails (429/offline). */
  const supportStaffUidMatchesCache =
    typeof window !== 'undefined' &&
    !!user?.id &&
    sessionStorage.getItem(SUPPORT_STAFF_UID_STORAGE_KEY) === user.id;

  const showSupportNav =
    initialized && !!user && ((authMeSucceeded && isSupportUser) || supportStaffUidMatchesCache);

  const brandHref = !user ? '/' : isOnboardingRoute ? '/onboarding' : '/analytics';

  return (
    <nav className="header-navigation">
      <Link to={brandHref} className="navbar-brand" aria-label="Relix home">
        <RelixLockup height={44} className="navbar-brand-lockup" />
      </Link>

      {initialized && user && currentOrg && !hideProductSwitcher ? <ProductSwitcher /> : null}

      <ul className="nav-menu">
        {navItems.map((item) => (
          <li
            key={item.id}
            id={
              item.id === 'calendar'
                ? 'nav-calendar-anchor'
                : item.id === 'releases'
                  ? 'nav-releases-anchor'
                  : undefined
            }
            className={navHighlightPath === item.path ? 'nav-coach-highlight' : undefined}
          >
            <NavLink
              to={item.path}
              className={({ isActive }) =>
                `nav-menu-link${item.id === 'delivery-review' ? ' nav-menu-link--delivery-review' : ''}${isActive ? ' active' : ''}`
              }
              end={item.path === '/analytics'}
            >
              {item.icon}
              {item.id === 'delivery-review' ? (
                <span className="nav-link-delivery-review__stack">
                  <span className="nav-link-delivery-review__title">{item.label}</span>
                  <span className="nav-link-delivery-review__sub">North star</span>
                </span>
              ) : (
                <span>{item.label}</span>
              )}
            </NavLink>
          </li>
        ))}
        {initialized && user ? (
          <li>
            <button type="button" className="nav-menu-link" onClick={handleLogout}>
              <LogOut className="w-5 h-5" />
              <span>Log out</span>
            </button>
          </li>
        ) : null}
        {showSupportNav ? (
          <li>
            <button
              type="button"
              className="nav-menu-link nav-menu-link--support"
              onClick={() => setShowSupportPanel(true)}
            >
              <Shield className="w-5 h-5" />
              <span>Support</span>
            </button>
          </li>
        ) : null}
        {showThemeToggle ? (
          <li>
            <ThemeToggle />
          </li>
        ) : null}
      </ul>

      <button
        type="button"
        className="nav-hamburger"
        onClick={() => setMobileMenuOpen((v) => !v)}
        aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={mobileMenuOpen}
      >
        {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {mobileMenuOpen && (
        <div className="nav-mobile-overlay" onClick={() => setMobileMenuOpen(false)} />
      )}

      <div
        ref={mobileMenuRef}
        className={`nav-mobile-drawer${mobileMenuOpen ? ' nav-mobile-drawer--open' : ''}`}
      >
        <ul className="nav-mobile-menu">
          {navItems.map((item) => (
            <li key={item.id}>
              <NavLink
                to={item.path}
                className={({ isActive }) =>
                  `nav-mobile-link${item.id === 'delivery-review' ? ' nav-mobile-link--delivery-review' : ''}${isActive ? ' active' : ''}`
                }
                end={item.path === '/analytics'}
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.icon}
                {item.id === 'delivery-review' ? (
                  <span className="nav-link-delivery-review__stack">
                    <span className="nav-link-delivery-review__title">{item.label}</span>
                    <span className="nav-link-delivery-review__sub">North star</span>
                  </span>
                ) : (
                  <span>{item.label}</span>
                )}
              </NavLink>
            </li>
          ))}
          {initialized && user ? (
            <li>
              <button
                type="button"
                className="nav-mobile-link"
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleLogout();
                }}
              >
                <LogOut className="w-5 h-5" />
                <span>Log out</span>
              </button>
            </li>
          ) : null}
          {showSupportNav ? (
            <li>
              <button
                type="button"
                className="nav-mobile-link"
                onClick={() => {
                  setMobileMenuOpen(false);
                  setShowSupportPanel(true);
                }}
              >
                <Shield className="w-5 h-5" />
                <span>Support</span>
              </button>
            </li>
          ) : null}
          {showThemeToggle ? (
            <li className="nav-mobile-theme">
              <ThemeToggle />
            </li>
          ) : null}
        </ul>
      </div>

      {showSupportPanel && <SupportPanel onClose={() => setShowSupportPanel(false)} />}
    </nav>
  );
};
